/**
 * @file index.tsx
 *
 * @fileoverview Shows a job teaser card.
 */

import { Job } from '~/jobiqo/types/types';
import Image from '~/jobiqo/components/Image';
import { useIntl } from 'react-intl';
import {
  getJobLocationToRender,
  getJobDatesToRender,
} from '~/jobiqo/services/jobs';
import { getTeaserImage } from '~/jobiqo/components/Job/utils/logo';
import Link from '~/jobiqo/components/Link';
import React, { useContext } from 'react';
import AnchorDiv from '~/jobiqo/components/AnchorDiv';
import BookmarkedJobsBookmark from '~/jobiqo/components/BookmarkedJobs/components/Bookmark';
import { getOrgProfileFromJob } from '~/jobiqo/components/Job/utils';
import { LayoutContext, LayoutContextInterface } from '~/jobiqo/context/layout';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { JobTeaser } from '~/vcl/organisms/03-job/02-teaser';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import JobTeaserCustomBadgeSlot from '~/jobiqo/components/Slots/JobTeaserCustomBadgeSlot';
import { useIsMobile } from '~/vcl/hooks/useIsMobile';
import { jobIsQuickApply } from '../utils/job_application';
import {
  getFormattedSalaryRangeOutput,
  isCurrentPageJobSearch,
} from '~/jobiqo/components/Job/Detail/utils';
import { useIsTablet } from '~/vcl/hooks/useIsTablet';
import { JobTeaserNoLogoIconSlot } from '~/jobiqo/components/Slots/JobTeaserNoLogoIconSlot';
import { JobTopJobIconSlot } from '~/jobiqo/components/Slots/JobTopJobIconSlot';
import { JobHighlightJobIconSlot } from '~/jobiqo/components/Slots/JobHighlightJobIconSlot';
import { OrganizationPromotedIconSlot } from '~/jobiqo/components/Slots/OrganizationPromotedIconSlot';
import { OrganizationIconSlot } from '~/jobiqo/components/Slots/OrganizationIconSlot';
import JobTeaserCustomInfoSlot from '~/jobiqo/components/Slots/JobTeaserCustomInfoSlot';

interface JobTeaserCardProps {
  job: Job;
  /**
   * If the card is highlighted.
   *
   * @default "false"
   */
  active?: boolean;
  /**
   * The flag to hide logo image.
   *
   * @default false
   */
  hideLogo?: boolean;
  /**
   * Prop to override default logo placement.
   */
  hideLogoReplacement?: React.ReactNode;
  /**
   * The route passed to next Router.push
   */
  route?: any;
  /**
   * The as argument passed to next Router.push
   */
  as?: string;
  /**
   * Prop to override default BookmarkedJobsBookmark component.
   *
   * @default BookmarkedJobsBookmark
   */
  BookmarkedComponent?: React.ReactNode;
  /**
   * Force context-based split-view routing outside the default jobs page.
   */
  shallowJobSearch?: boolean;
}

export const JobTeaserCard = ({
  job,
  active = false,
  hideLogo = false,
  route,
  as,
  BookmarkedComponent,
  hideLogoReplacement = null,
  shallowJobSearch = false,
}: JobTeaserCardProps) => {
  const intl = useIntl();
  const { JOBS, ORGANIZATIONS, RESUME } = useFrontendConfig();
  const APPLICANT_FEATURE_ACTIVE = RESUME.APPLICANT_FEATURE_ACTIVE;
  const jobRoute = route ?? job.urlNoPrefix ?? job.url.path;
  const {
    toggleSidenavCollapsed,
    getSidenavCollapsed,
  }: LayoutContextInterface = useContext(LayoutContext);
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  const isJobSearchPage = isCurrentPageJobSearch();
  const shouldScrollToTop = !shallowJobSearch && (isMobile || !isJobSearchPage);
  const orgProfile = getOrgProfileFromJob(job);
  const isJobInactive =
    job.workflowState && job.workflowState.value !== 'published';
  const teaserImageSrc = getTeaserImage(job);
  const teaserLogoAlt = intl.formatMessage(
    { id: 'job_teaser_logo_title', defaultMessage: 'Logo for job {jobTitle}' },
    { jobTitle: job.title },
  );
  const salaryRangeFreeField =
    'salaryRangeFree' in job ? job.salaryRangeFree : undefined;
  const salaryRangeFreeOutput = getFormattedSalaryRangeOutput(
    salaryRangeFreeField ?? {},
    intl,
  );
  // Get salary value from taxonomy term based or custom salary range field.
  const salary = job.salaryRange?.[0]?.label
    ? job.salaryRange?.[0]?.label
    : salaryRangeFreeOutput
    ? salaryRangeFreeOutput
    : null;

  return (
    <AnchorDiv
      title={job.title}
      data-test-id={`job-teaser-${job.id} ${
        jobIsQuickApply(job) ? 'quick-apply' : ''
      }`}
      route={jobRoute}
      as={as}
      scroll={shouldScrollToTop}
      shallowJobSearch={shallowJobSearch}
      onClickCallback={(target) => {
        // If the job has an absolute URL set, open the job in a new tab.

        if (target?.id !== 'job-teaser-company-link' && job.absoluteUrl) {
          window.open(job.absoluteUrl, '_blank');
          return;
        }
        // Close the sidebar on tablets when we click the job teaser.
        if (!getSidenavCollapsed() && isTablet) {
          toggleSidenavCollapsed();
        }
      }}
    >
      <JobTeaser
        {...(!hideLogo && {
          image: teaserImageSrc ? (
            <Image
              src={teaserImageSrc}
              height={JOBS.JOB_TEASER.LOGO_WIDTH}
              width={JOBS.JOB_TEASER.LOGO_WIDTH}
              alt={teaserLogoAlt}
              convertFormat={false}
            />
          ) : (
            <FlexItems>
              <JobTeaserNoLogoIconSlot job={job} />
            </FlexItems>
          ),
        })}
        {...(hideLogo &&
          hideLogoReplacement && {
            image: hideLogoReplacement,
          })}
        title={
          // Link to the job url path, e.g.: /job/property-tax-agent.
          // In the AnchorDiv from above, we are still using a query
          // string in the browser URL, e.g.: /jobs?jobId=2.
          <Link
            scroll={isMobile}
            href={job.url.path}
            prefetch={false}
            data-avoid-redirect={true}
            onClick={(e) => {
              // We want to stop the anchor element redirect and
              // let the AnchorDiv component from above handle the redirect.
              e.preventDefault();
            }}
          >
            {job.title}
          </Link>
        }
        hideIconsMobile={JOBS.JOB_TEASER.HIDE_ICONS_MOBILE}
        featured={JOBS.TEASER_SHOW_FEATURED_INDICATORS && job.isHighlight}
        featuredLabel={intl.formatMessage({
          id: 'job_featured',
          defaultMessage: 'Featured job',
        })}
        topJob={job.isTopJob}
        topJobLabel={intl.formatMessage({
          id: 'top_job',
          defaultMessage: 'Top job',
        })}
        featuredJobIcon={
          <JobHighlightJobIconSlot job={job} width="25" height="25" />
        }
        topJobIcon={<JobTopJobIconSlot job={job} width="25" height="25" />}
        organizationIcon={<OrganizationIconSlot />}
        promotedOrganizationIcon={<OrganizationPromotedIconSlot />}
        premiumCompany={
          ORGANIZATIONS.SHOW_PREMIUM_INDICATIONS &&
          job.organizationProfile?.['isPremium']
        }
        {...(job.remoteOptions && job.remoteOptions?.length > 0
          ? {
              remote: job?.remoteOptions
                .map((option) => option?.label)
                .join(' | '),
            }
          : null)}
        company={
          job.organizationProfile && orgProfile.isPublished ? (
            <Link
              id="job-teaser-company-link"
              href={
                job.absoluteOrgUrl ??
                job.organizationProfile?.urlNoPrefix ??
                job.organizationProfile?.url?.path
              }
              {...(job.absoluteOrgUrl && { target: '_blank' })}
            >
              {orgProfile.label}
            </Link>
          ) : (
            orgProfile.label
          )
        }
        locations={getJobLocationToRender(job)}
        dates={getJobDatesToRender(job, intl, false, true)}
        active={active}
        inactive={isJobInactive}
        Bookmark={
          APPLICANT_FEATURE_ACTIVE
            ? BookmarkedComponent ?? (
                <BookmarkedJobsBookmark
                  job={job}
                  bookmarkMutationCallback={(isBookmarked) => {
                    job.bookmarked = isBookmarked;
                  }}
                />
              )
            : null
        }
        customBadge={<JobTeaserCustomBadgeSlot job={job} />}
        quickApplyText={
          jobIsQuickApply(job)
            ? intl.formatMessage({
                id: 'job_teaser_quick_apply_text',
                defaultMessage: 'Quick Apply',
              })
            : undefined
        }
        salary={salary ?? undefined}
        sourcePortals={job.sourcePortals as string[]}
        customInfo={<JobTeaserCustomInfoSlot job={job} />}
      />
    </AnchorDiv>
  );
};
export default JobTeaserCard;
